import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import IconPicker from "@/components/feature/IconPicker";
import { uploadFileToCloud } from "@/utils/cloudImageUpload";
import { defaultNavLinks, type NavLink } from "@/data/siteContent";

export default function Navbar() {
  const { isAdmin, logout, getLogoFont, logoConfig, setLogoConfig } = useAdmin();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoEditor, setShowLogoEditor] = useState(false);
  const [logoDraft, setLogoDraft] = useState(logoConfig);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [linkDraft, setLinkDraft] = useState<Partial<NavLink>>({});
  const [addingNew, setAddingNew] = useState(false);
  const [newLink, setNewLink] = useState<Partial<NavLink>>({ label: "", href: "", external: false, inTopBar: false });
  const logoFont = getLogoFont();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const navigate = useNavigate();
  const [links, setLinks] = useAdminStorage<NavLink[]>("ma_nav_links", defaultNavLinks);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string, external?: boolean) => {
    setMenuOpen(false);
    if (external) {
      navigate(href);
      return;
    }
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const removeLink = (id: string) => {
    setLinks(links.filter((l) => l.id !== id));
  };

  const startEdit = (link: NavLink) => {
    setEditingLinkId(link.id);
    setLinkDraft({ ...link });
  };

  const saveEdit = () => {
    if (!editingLinkId) return;
    setLinks(links.map((l) => l.id === editingLinkId ? { ...l, ...linkDraft } as NavLink : l));
    setEditingLinkId(null);
    setLinkDraft({});
  };

  const addLink = () => {
    if (!newLink.label || !newLink.href) return;
    const id = `nav-${Date.now()}`;
    setLinks([...links, { id, label: newLink.label!, href: newLink.href!, external: !!newLink.external, inTopBar: !!newLink.inTopBar }]);
    setNewLink({ label: "", href: "", external: false, inTopBar: false });
    setAddingNew(false);
  };

  const topLinks = links.filter((l) => l.inTopBar);
  const hamburgerLinks = links.filter((l) => !l.inTopBar);

  // On sub-pages (not home), always use scrolled/solid style since there's no hero image
  const isSubPage = window.location.pathname !== "/";
  const effectiveScrolled = scrolled || isSubPage;

  const navTextCls = (s: boolean) =>
    s
      ? isDark ? "text-gray-300 hover:text-pink-400" : "text-gray-700 hover:text-pink-500"
      : "text-white hover:text-pink-200";

  const inputCls = "border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pink-400 w-full";

  const saveLogo = () => {
    setLogoConfig(logoDraft);
    setShowLogoEditor(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        effectiveScrolled
          ? isDark ? "bg-gray-900 shadow-sm" : "bg-white shadow-sm"
          : "bg-transparent"
      }`}
    >
      {/* Admin bar */}
      {isAdmin && (
        <div className="w-full bg-gray-900 text-gray-400 text-xs flex items-center justify-between px-6 py-1.5">
          <span className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-green-400"></i>
            Admin mode — edit controls visible. Use the Save bar at the bottom to commit changes.
          </span>
          <button
            onClick={() => logout()}
            className="hover:text-white transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-logout-box-r-line"></i>
            Log out
          </button>
        </div>
      )}

      <div className="px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNav("#home"); }}
          className="flex items-center gap-2 cursor-pointer flex-shrink-0 relative group"
        >
          {logoConfig.mode === "image" && logoConfig.imageUrl ? (
            <img
              src={logoConfig.imageUrl}
              alt="Logo"
              className="h-8 w-auto object-contain"
            />
          ) : (
            <>
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-400">
                <i className={`${logoConfig.icon} text-white text-sm`}></i>
              </div>
              <span
                className={`font-bold text-xl tracking-tight transition-colors ${
                  effectiveScrolled ? isDark ? "text-white" : "text-gray-900" : "text-white"
                }`}
                style={{ fontFamily: logoFont.fontFamily, ...logoFont.style }}
              >
                {logoConfig.text}
                {logoConfig.suffixText && (
                  <span
                    style={{
                      color: logoConfig.suffixColor,
                      fontStyle: logoConfig.suffixItalic ? "italic" : undefined,
                      fontWeight: logoConfig.suffixBold ? 700 : undefined,
                    }}
                  >
                    {logoConfig.suffixText}
                  </span>
                )}
              </span>
            </>
          )}

          {/* Admin logo editor */}
          {isAdmin && (
            <div className="hidden group-hover:flex items-center gap-1 absolute -top-7 left-0 bg-gray-900 rounded-lg px-2 py-1 z-20 whitespace-nowrap">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowLogoEditor(true); }}
                className="text-pink-400 hover:text-pink-300 cursor-pointer p-0.5"
                title="Edit logo"
              >
                <i className="ri-edit-line text-xs"></i>
              </button>
            </div>
          )}
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {topLinks.map((link) => (
            <div key={link.id} className="relative group flex items-center gap-1">
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href, link.external); }}
                className={`text-sm font-semibold tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap ${navTextCls(effectiveScrolled)}`}
              >
                {link.label}
              </a>
              {isAdmin && (
                <div className="hidden group-hover:flex items-center gap-0.5 absolute -top-6 left-0 bg-gray-900 rounded-lg px-1.5 py-1 z-20 whitespace-nowrap">
                  <button onClick={() => startEdit(link)} className="text-pink-400 hover:text-pink-300 cursor-pointer p-0.5" title="Edit link">
                    <i className="ri-edit-line text-xs"></i>
                  </button>
                  <button onClick={() => removeLink(link.id)} className="text-red-400 hover:text-red-300 cursor-pointer p-0.5" title="Remove link">
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Dark mode toggle */}
          <button
            onClick={toggleDark}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              effectiveScrolled
                ? isDark ? "bg-gray-700 text-yellow-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                : "bg-white/20 text-white hover:bg-white/30"
            }`}
          >
            <i className={isDark ? "ri-sun-line text-lg" : "ri-moon-line text-lg"}></i>
          </button>

          {/* Apply Now button */}
          <a
            href="#application"
            onClick={(e) => { e.preventDefault(); handleNav("#application"); }}
            className="bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold px-5 py-2 rounded-full whitespace-nowrap transition-colors cursor-pointer"
          >
            Apply Now
          </a>

          {/* Hamburger for secondary links */}
          <button
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              effectiveScrolled
                ? isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                : "bg-white/20 text-white hover:bg-white/30"
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="More pages"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
          </button>

          {isAdmin ? (
            <button
              onClick={() => navigate("/admin")}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                effectiveScrolled
                  ? isDark ? "bg-gray-700 text-pink-400 hover:bg-gray-600" : "bg-gray-100 text-pink-500 hover:bg-pink-50"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
              title="Go to Admin Center"
            >
              <i className="ri-settings-3-line"></i>
              Admin
            </button>
          ) : (
            <button
              onClick={() => navigate("/admin")}
              className={`text-xs transition-colors cursor-pointer opacity-20 hover:opacity-60 whitespace-nowrap ${
                effectiveScrolled ? (isDark ? "text-gray-400" : "text-gray-400") : "text-white"
              }`}
              title="Admin"
              aria-label="Admin login"
            >
              <i className="ri-lock-line"></i>
            </button>
          )}
        </nav>

        {/* Mobile right side */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleDark}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              effectiveScrolled
                ? isDark ? "bg-gray-700 text-yellow-300" : "bg-gray-100 text-gray-600"
                : "bg-white/20 text-white"
            }`}
          >
            <i className={isDark ? "ri-sun-line text-lg" : "ri-moon-line text-lg"}></i>
          </button>
          <a
            href="#application"
            onClick={(e) => { e.preventDefault(); handleNav("#application"); }}
            className="bg-pink-500 text-white text-xs font-bold px-3 py-2 rounded-full whitespace-nowrap cursor-pointer"
          >
            Apply
          </a>
          <button
            className={`text-2xl cursor-pointer ${effectiveScrolled ? (isDark ? "text-white" : "text-gray-800") : "text-white"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
          </button>
        </div>
      </div>

      {/* Dropdown menu */}
      {menuOpen && (
        <div
          className={`absolute top-full right-0 md:right-6 w-full md:w-64 border rounded-2xl overflow-y-auto ${
            isDark ? "bg-gray-900 border-gray-700" : "bg-white border-pink-100"
          }`}
          style={{ maxHeight: "calc(100vh - 120px)" }}
        >
          {/* Mobile: show top links too */}
          <div className="md:hidden">
            {topLinks.map((link) => (
              <div key={link.id} className={`flex items-center border-b ${isDark ? "border-gray-800" : "border-pink-50"}`}>
                <a
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNav(link.href, link.external); }}
                  className={`flex-1 flex items-center gap-3 px-5 py-3.5 text-sm font-semibold uppercase tracking-widest cursor-pointer transition-colors ${
                    isDark ? "text-gray-300 hover:text-pink-400 hover:bg-gray-800" : "text-gray-700 hover:text-pink-500 hover:bg-pink-50"
                  }`}
                >
                  {link.label}
                </a>
                {isAdmin && (
                  <div className="flex items-center gap-1 pr-3">
                    <button onClick={() => startEdit(link)} className="w-6 h-6 flex items-center justify-center text-pink-400 hover:text-pink-300 cursor-pointer" title="Edit">
                      <i className="ri-edit-line text-xs"></i>
                    </button>
                    <button onClick={() => removeLink(link.id)} className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300 cursor-pointer" title="Remove">
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Hamburger links */}
          {hamburgerLinks.map((link) => (
            <div key={link.id} className={`flex items-center border-b last:border-b-0 ${isDark ? "border-gray-800" : "border-pink-50"}`}>
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href, link.external); }}
                className={`flex-1 flex items-center gap-3 px-5 py-3.5 text-sm font-medium cursor-pointer transition-colors ${
                  isDark ? "text-gray-300 hover:text-pink-400 hover:bg-gray-800" : "text-gray-600 hover:text-pink-500 hover:bg-pink-50"
                }`}
              >
                {link.label}
              </a>
              {isAdmin && (
                <div className="flex items-center gap-1 pr-3">
                  <button onClick={() => startEdit(link)} className="w-6 h-6 flex items-center justify-center text-pink-400 hover:text-pink-300 cursor-pointer" title="Edit">
                    <i className="ri-edit-line text-xs"></i>
                  </button>
                  <button onClick={() => removeLink(link.id)} className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300 cursor-pointer" title="Remove">
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Admin: Add new link */}
          {isAdmin && (
            <div className={`border-t ${isDark ? "border-gray-700" : "border-pink-100"}`}>
              {!addingNew ? (
                <button
                  onClick={() => setAddingNew(true)}
                  className={`w-full flex items-center gap-2 px-5 py-3 text-xs font-semibold cursor-pointer transition-colors ${
                    isDark ? "text-green-400 hover:bg-gray-800" : "text-green-600 hover:bg-green-50"
                  }`}
                >
                  <i className="ri-add-line"></i>
                  Add Navigation Link
                </button>
              ) : (
                <div className="px-4 py-3 space-y-2">
                  <input
                    type="text"
                    placeholder="Label (e.g. Blog)"
                    value={newLink.label || ""}
                    onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    type="text"
                    placeholder="URL (e.g. #section or /page)"
                    value={newLink.href || ""}
                    onChange={(e) => setNewLink({ ...newLink, href: e.target.value })}
                    className={inputCls}
                  />
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={!!newLink.external} onChange={(e) => setNewLink({ ...newLink, external: e.target.checked })} className="accent-pink-500" />
                      External page
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={!!newLink.inTopBar} onChange={(e) => setNewLink({ ...newLink, inTopBar: e.target.checked })} className="accent-pink-500" />
                      Show in top bar
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={addLink} className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap">Add</button>
                    <button onClick={() => setAddingNew(false)} className="text-gray-400 text-xs cursor-pointer whitespace-nowrap">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Logo editor modal */}
      {showLogoEditor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setShowLogoEditor(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-96 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base">Edit Logo</h3>

            {/* Mode toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setLogoDraft({ ...logoDraft, mode: "icon-text" })}
                className={`flex-1 text-xs font-semibold py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  logoDraft.mode === "icon-text" ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <i className="ri-font-size mr-1"></i>Icon + Text
              </button>
              <button
                onClick={() => setLogoDraft({ ...logoDraft, mode: "image" })}
                className={`flex-1 text-xs font-semibold py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  logoDraft.mode === "image" ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <i className="ri-image-line mr-1"></i>Image Logo
              </button>
            </div>

            {logoDraft.mode === "icon-text" ? (
              <>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Icon</label>
                  <div className="flex items-center gap-2">
                    <IconPicker
                      value={logoDraft.icon}
                      onChange={(icon) => setLogoDraft({ ...logoDraft, icon })}
                    />
                    <span className="text-gray-500 text-xs">{logoDraft.icon}</span>
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Logo Text (before suffix)</label>
                  <input
                    type="text"
                    value={logoDraft.text}
                    onChange={(e) => setLogoDraft({ ...logoDraft, text: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Suffix Text (e.g. "co")</label>
                  <input
                    type="text"
                    value={logoDraft.suffixText || ""}
                    onChange={(e) => setLogoDraft({ ...logoDraft, suffixText: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-gray-400 text-xs mb-1 block">Suffix Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={logoDraft.suffixColor || "#22c55e"}
                        onChange={(e) => setLogoDraft({ ...logoDraft, suffixColor: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                      />
                      <span className="text-gray-500 text-xs">{logoDraft.suffixColor || "#22c55e"}</span>
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer text-gray-400 text-xs">
                    <input
                      type="checkbox"
                      checked={!!logoDraft.suffixItalic}
                      onChange={(e) => setLogoDraft({ ...logoDraft, suffixItalic: e.target.checked })}
                      className="accent-pink-500"
                    />
                    Italic
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-gray-400 text-xs">
                    <input
                      type="checkbox"
                      checked={!!logoDraft.suffixBold}
                      onChange={(e) => setLogoDraft({ ...logoDraft, suffixBold: e.target.checked })}
                      className="accent-pink-500"
                    />
                    Bold
                  </label>
                </div>
                {/* Live preview */}
                <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-400 flex-shrink-0">
                    <i className={`${logoDraft.icon} text-white text-sm`}></i>
                  </div>
                  <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: logoFont.fontFamily, ...logoFont.style }}>
                    {logoDraft.text}
                    {logoDraft.suffixText && (
                      <span
                        style={{
                          color: logoDraft.suffixColor || "#22c55e",
                          fontStyle: logoDraft.suffixItalic ? "italic" : undefined,
                          fontWeight: logoDraft.suffixBold ? 700 : undefined,
                        }}
                      >
                        {logoDraft.suffixText}
                      </span>
                    )}
                  </span>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                {/* Upload from device */}
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Upload Logo Image</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => logoFileRef.current?.click()}
                      disabled={logoUploading}
                      className="flex items-center gap-1.5 text-xs font-bold bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                    >
                      {logoUploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                      {logoUploading ? "Uploading..." : "Upload File"}
                    </button>
                    <input
                      ref={logoFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        e.target.value = "";
                        setLogoUploading(true);
                        try {
                          const url = await uploadFileToCloud(file);
                          setLogoDraft({ ...logoDraft, imageUrl: url });
                        } catch (err) {
                          console.error("Logo upload failed:", err);
                        } finally {
                          setLogoUploading(false);
                        }
                      }}
                    />
                  </div>
                </div>
                {/* OR paste URL */}
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Or paste an image URL</label>
                  <input
                    type="text"
                    value={logoDraft.imageUrl}
                    onChange={(e) => setLogoDraft({ ...logoDraft, imageUrl: e.target.value })}
                    placeholder="https://... (optional)"
                    className={inputCls}
                  />
                  <p className="text-gray-500 text-xs mt-1">Transparent PNG works best. Leave blank to use no image.</p>
                </div>
                {/* Revert to current saved logo */}
                {logoConfig.imageUrl && logoDraft.imageUrl !== logoConfig.imageUrl && (
                  <button
                    type="button"
                    onClick={() => setLogoDraft({ ...logoDraft, imageUrl: logoConfig.imageUrl })}
                    className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-arrow-go-back-line"></i> Revert to current logo
                  </button>
                )}
                {logoDraft.imageUrl && (
                  <div className="mt-2 p-2 bg-gray-800 rounded-lg">
                    <img src={logoDraft.imageUrl} alt="Logo preview" className="h-8 w-auto object-contain mx-auto" />
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={saveLogo} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Save</button>
              <button onClick={() => { setLogoDraft(logoConfig); setShowLogoEditor(false); }} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit link modal */}
      {editingLinkId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setEditingLinkId(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-80 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base">Edit Navigation Link</h3>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Label</label>
              <input
                type="text"
                value={linkDraft.label || ""}
                onChange={(e) => setLinkDraft({ ...linkDraft, label: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">URL / Anchor</label>
              <input
                type="text"
                value={linkDraft.href || ""}
                onChange={(e) => setLinkDraft({ ...linkDraft, href: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!linkDraft.external} onChange={(e) => setLinkDraft({ ...linkDraft, external: e.target.checked })} className="accent-pink-500" />
                External page
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!linkDraft.inTopBar} onChange={(e) => setLinkDraft({ ...linkDraft, inTopBar: e.target.checked })} className="accent-pink-500" />
                Show in top bar
              </label>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={saveEdit} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Save</button>
              <button onClick={() => setEditingLinkId(null)} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
